import { initClient, flashMessageAPI } from '@kidslabs/module-core';
import { type ApolloClient, type FetchResult, type NormalizedCacheObject } from '@apollo/client';
import type { CartItemReference } from '../models';
import type { CartItemReferenceError } from '../models/types/cart-item-reference';
import { ActionType } from '../models/types/cart-item-reference-action';
import {
    AddConfigurableProductsToCartDocument,
    type AddConfigurableProductsToCartMutation,
    type AddConfigurableProductsToCartOutput,
    AddGiftsToCartDocument,
    type AddGiftsToCartInput,
    type AddGiftsToCartMutation,
    AddSimpleProductsToCartDocument,
    type AddSimpleProductsToCartMutation,
    type AddSimpleProductsToCartOutput,
    type Cart,
    type CartItemInterface,
    type CartItemPrices,
    CartItemsOnlyDocument,
    type CartSyncActionInput,
    type CartSyncItemInput,
    type CartSyncLogicInput,
    type ConfigurableCartItem,
    type PickedGiftInput,
    type SimpleCartItem,
    SynchronizeCartDocument,
    type SynchronizeCartInput,
    type SynchronizeCartMutation,
    UpdateCartItemsDocument,
    type UpdateCartItemsInput,
} from '../types/front.graphql';
import { cartItemReferenceSynchronizationStore$ } from '../stores/cart-item-reference-synchronization.store';
import { cartItemReferenceSelectedToProcessLogicsStore$ } from '../stores';
import { CartItemReferenceReconciliationService } from './cart-item-reference-reconciliation.service';
import { getReferencesToSync } from './reconcile-cart-references.service';
import { cartStoreAPI } from '../stores/cart-store.public-api';
import { cartItemReferenceStoreAPI } from '../stores/cart-item-reference-store.public-api';
import { setInternalUpdate } from '../stores/cart-item-reference.store';
import { Semaphore } from '../utils/semaphore.util';

/**
 * ════════════════════════════════════════════════════════════════════════════
 * STORE MUTATIONS COLLECTOR
 * ════════════════════════════════════════════════════════════════════════════
 * Collects all store mutations during sync execution without applying them.
 * Mutations are applied atomically AFTER semaphore release.
 *
 * ARCHITECTURE PRINCIPLE: Collect-and-Apply Pattern
 * - Semaphore-protected code is PURE (no store mutations)
 * - All mutations collected during execution
 * - Applied atomically after execution completes
 * - Prevents race conditions and recursive triggers
 */

interface StoreMutations {
    // Cart Item Reference Mutations
    cartItemReferencesToUpdate: CartItemReference[];
    cartItemReferencesToAdd: CartItemReference[];
    cartItemReferencesToSet: CartItemReference[] | null;

    // Cart Mutations
    cartToUpdate: Cart | null;
    cartId: string; // Cart ID for updating references after sync

    // Metadata
    syncedAt: number;
    error?: Error;
}

class MutationCollector {
    private mutations: StoreMutations;

    constructor(cartId: string) {
        this.mutations = {
            cartItemReferencesToUpdate: [],
            cartItemReferencesToAdd: [],
            cartItemReferencesToSet: null,
            cartToUpdate: null,
            cartId,
            syncedAt: Date.now(),
        };
    }

    addReferenceUpdate(ref: CartItemReference): void {
        this.mutations.cartItemReferencesToUpdate.push(ref);
    }

    addReferenceAddition(ref: CartItemReference): void {
        this.mutations.cartItemReferencesToAdd.push(ref);
    }

    setAllReferences(refs: CartItemReference[]): void {
        this.mutations.cartItemReferencesToSet = refs;
    }

    setCart(cart: Cart): void {
        this.mutations.cartToUpdate = cart;
    }

    getMutations(): StoreMutations {
        return this.mutations;
    }
}

export class CartItemReferenceSynchronizationService {
    private client: ApolloClient<NormalizedCacheObject> | undefined = undefined;

    private reconciliationService: CartItemReferenceReconciliationService;

    // ========================================================================
    // SEMAPHORE - PREVENT CONCURRENT SYNC OPERATIONS
    // ========================================================================
    // Use STATIC semaphore with 1 permit to ensure sequential execution (mutex behavior)
    // Only one sync operation can run at a time, others will be queued.
    // CRITICAL: Must be static so ALL instances share the same semaphore.
    // Previously was an instance field, meaning each `new CartItemReferenceSynchronizationService()`
    // got its own semaphore — providing zero concurrency protection.
    private static syncSemaphore: Semaphore = new Semaphore(1);

    // ========================================================================
    // SYNC STATE FLAG - PREVENT RECURSIVE SYNC TRIGGERS
    // ========================================================================
    // Flag to track if sync is currently executing
    // Used to prevent store updates during sync from triggering another sync
    private static isCurrentlySyncing = false;

    constructor() {
        this.reconciliationService = new CartItemReferenceReconciliationService();
    }

    private async getClient(): Promise<ApolloClient<NormalizedCacheObject> | undefined> {
        if (!this.client) {
            this.client = (await initClient({ uploadClient: false })).client;
        }
        return this.client;
    }

    // ════════════════════════════════════════════════════════════════════════════
    // NEW SYNCHRONIZE CART API - SINGLE API CALL
    // ════════════════════════════════════════════════════════════════════════════

    /**
     * Convert CartItemReference to CartSyncItemInput for SynchronizeCart mutation
     *
     * CRITICAL: Ensures all required fields are present and correctly typed
     * No usage of 'any' type - full type safety
     *
     * DATA MAPPING:
     * - CartItemReference.logics → CartSyncLogicInput[]
     * - CartItemReferenceLogic.actions → CartSyncActionInput[]
     * - CartItemReferenceAction.actionProduct → CartSyncActionProductInput
     *
     * @param reference - Local cart item reference
     * @returns CartSyncItemInput for GraphQL mutation
     */
    private convertReferenceToSyncItem(reference: CartItemReference): CartSyncItemInput {
        // Get SKU from cart item
        const cartItem = reference.cartItem;
        const productSku = cartItem?.product?.sku ?? '';

        // Get parent SKU for configurable products
        const parentSku = reference.parentProduct?.sku ?? undefined;

        // Get variant SKU for configurable products
        const configurableCartItem = cartItem as ConfigurableCartItem | undefined;
        const variantSkuFromCartItem = configurableCartItem?.configured_variant?.sku;

        // Determine final SKU with fallback chain:
        // 1. configured_variant.sku (from local ConfigurableCartItem - most reliable for physical items)
        // 2. reference.sku (preserved from server's cart_item_reference.sku — ALWAYS variant SKU)
        // 3. product.sku (for simple products, or fallback for configurable)
        //
        // CRITICAL: For configurable products, product.sku is the PARENT SKU.
        // After cartSyncReferences fetch, configured_variant may be missing
        // because ConfigurableLocalCartItem doesn't always include it.
        // reference.sku preserves the correct variant SKU across syncs.
        let finalSku = variantSkuFromCartItem ?? reference.sku ?? productSku;

        // ════════════════════════════════════════════════════════════════════
        // SAFETY GUARD: Detect parent/child SKU collision
        // ════════════════════════════════════════════════════════════════════
        // If finalSku equals parentSku, the fallback chain resolved to the
        // PARENT SKU instead of the CHILD/VARIANT SKU. This would corrupt
        // the cart because CartReconciliationProcessor maps by SKU.
        if (parentSku && finalSku === parentSku) {
            console.error(
                '[CartSync][CRITICAL] SKU collision detected: finalSku equals parentSku. ' +
                    'This would corrupt the cart. Attempting recovery via reference.sku.',
                {
                    finalSku,
                    parentSku,
                    referenceSku: reference.sku,
                    variantSkuFromCartItem,
                    productSku,
                    referenceId: reference.cartItemReferenceId,
                }
            );

            // Recovery: Use reference.sku if it differs from parentSku
            if (reference.sku && reference.sku !== parentSku) {
                finalSku = reference.sku;
                console.warn('[CartSync] Recovered variant SKU from reference.sku:', finalSku);
            } else {
                // Cannot determine variant SKU — skip this item to prevent corruption
                throw new Error(
                    `[CartSync] Cannot determine variant SKU for configurable product. ` +
                        `parentSku=${parentSku}, referenceSku=${reference.sku}, productSku=${productSku}. ` +
                        `ReferenceId=${reference.cartItemReferenceId}`
                );
            }
        }

        // Convert logics to CartSyncLogicInput array
        const logics: CartSyncLogicInput[] = reference.logics.map(logic => {
            // Convert actions to CartSyncActionInput array
            const actions: CartSyncActionInput[] = logic.actions.map(action => {
                // Generate unique ID if not present
                const actionId = action.id ?? `action-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

                const actionInput: CartSyncActionInput = {
                    id: actionId,
                    actionType: action.actionType,
                    actionValue: action.actionValue ?? undefined,
                    actionQty: action.actionQty ?? undefined,
                };

                // ════════════════════════════════════════════════════════════════════
                // GIFT PRODUCT HANDLING - Critical for gift selection
                // ════════════════════════════════════════════════════════════════════
                // For gift actions, we need to send the gift product SKU
                // actionProduct can be ProductInterface with sku property
                if (action.actionType === ActionType.GIFT && action.actionProduct) {
                    const giftSku = action.actionProduct.sku;
                    if (giftSku) {
                        actionInput.actionProduct = {
                            sku: giftSku,
                        };
                    }
                }

                return actionInput;
            });

            // ════════════════════════════════════════════════════════════════════
            // ALLOCATED QUANTITY CALCULATION
            // ════════════════════════════════════════════════════════════════════
            // Priority:
            // 1. Use logic.allocatedQty if explicitly set (from gift selection flow)
            // 2. Fall back to reference.qty (entire reference quantity applies to this logic)
            //
            // allocatedQty = number of purchased items allocated to THIS rule
            // for gift calculation. When multiple rules apply, the total purchase
            // qty (reference.qty) is split across rules.
            //
            // Example: User buys 14 items. Rule 1 and Rule 2 both apply.
            //   Rule 1 ("buy 3 get 1 gift"): allocatedQty = 8 → floor(8/3) = 2 gifts
            //   Rule 2 ("buy 2 get 1 gift"): allocatedQty = 6 → floor(6/2) = 3 gifts
            //
            // Fallback to reference.qty when only one rule applies or not explicitly set.
            const allocatedQty = logic.allocatedQty ?? reference.qty;

            // Generate unique ID if not present
            const logicId = logic.id ?? `logic-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

            const logicInput: CartSyncLogicInput = {
                id: logicId,
                logicId: String(logic.logicId),
                logicType: logic.logicType,
                sessionId: logic.sessionId ?? '',
                allocatedQty,
                actions,
            };
            return logicInput;
        });

        // Use cartItemId if exists, otherwise use cartItemReferenceId (UUID format)
        // Note: Server expects UUID format, not `local-` prefix
        const localId = reference.cartItemId ?? reference.cartItemReferenceId;

        return {
            cartItemReferenceId: reference.cartItemReferenceId,
            localId,
            sku: finalSku,
            parent_sku: parentSku,
            quantity: reference.qty,
            isSelected: reference.isSelected,
            logics,
        };
    }

    /**
     * Extract structured item_errors from GraphQL error extensions.
     *
     * When the checkout-service detects gift validation errors from Magento,
     * it throws a GraphQLError with code CART_SYNC_VALIDATION_FAILED and
     * attaches parsed item_errors in extensions. This method extracts those
     * so we can display them on the correct cart item reference lines.
     */
    private extractItemErrorsFromGraphQLErrors(
        errors: ReadonlyArray<{ message: string; extensions?: Record<string, unknown> }>
    ): Array<{ cart_item_reference_id: string; sku?: string; error_code: string; message: string }> {
        const allItemErrors: Array<{
            cart_item_reference_id: string;
            sku?: string;
            error_code: string;
            message: string;
        }> = [];

        for (const error of errors) {
            const extensions = error.extensions;
            if (extensions?.code === 'CART_SYNC_VALIDATION_FAILED' && Array.isArray(extensions?.item_errors)) {
                for (const itemError of extensions.item_errors as Array<Record<string, unknown>>) {
                    if (typeof itemError.cart_item_reference_id === 'string' && typeof itemError.message === 'string') {
                        allItemErrors.push({
                            cart_item_reference_id: itemError.cart_item_reference_id,
                            sku: typeof itemError.sku === 'string' ? itemError.sku : undefined,
                            error_code: typeof itemError.error_code === 'string' ? itemError.error_code : 'UNKNOWN',
                            message: itemError.message,
                        });
                    }
                }
            }
        }

        return allItemErrors;
    }

    /**
     * Sync cart using new SynchronizeCart API
     *
     * This method replaces multiple API calls with a single SynchronizeCart mutation.
     * Server handles all calculations and returns the updated cart state.
     *
     * BENEFITS:
     * - Single network request instead of multiple
     * - Server-side validation and calculation
     * - Atomic operation - all or nothing
     * - Reduced complexity and race conditions
     *
     * @param cartId - The masked cart ID
     * @param cartItemReferences - All local cart item references to sync
     * @returns Promise<void>
     */
    async syncWithSynchronizeCartAPI(
        cartId: string,
        cartItemReferences: CartItemReference[],
        onBeforeApplyMutations?: () => void
    ): Promise<void> {
        if (!cartId) {
            throw new Error('[CartSync] Cart ID is required for synchronization');
        }

        const client = await this.getClient();
        if (!client) {
            throw new Error('[CartSync] Apollo client not initialized');
        }

        // Use STATIC semaphore to prevent concurrent sync operations
        const mutations = await CartItemReferenceSynchronizationService.syncSemaphore.execute(async () => {
            CartItemReferenceSynchronizationService.isCurrentlySyncing = true;
            const mutationCollector = new MutationCollector(cartId);

            try {
                cartItemReferenceSynchronizationStore$.syncStatus.set('loading');

                // ════════════════════════════════════════════════════════════════════
                // STEP 1: Convert ALL references to sync items
                // ════════════════════════════════════════════════════════════════════
                // Send the complete set of references so the server has the full picture.
                // Each item carries its own isSelected flag — the server decides how to
                // handle selected vs unselected items (add/update vs remove/skip).
                const syncItems: CartSyncItemInput[] = cartItemReferences.map(ref =>
                    this.convertReferenceToSyncItem(ref)
                );

                // ════════════════════════════════════════════════════════════════════
                // STEP 2: Execute SynchronizeCart mutation
                // ════════════════════════════════════════════════════════════════════
                const input: SynchronizeCartInput = {
                    cart_id: cartId,
                    items: syncItems,
                };

                const result: FetchResult<SynchronizeCartMutation> = await client.mutate({
                    mutation: SynchronizeCartDocument,
                    variables: { input },
                });

                if (result.errors && result.errors.length > 0) {
                    // ────────────────────────────────────────────────────────────────
                    // Check if the GraphQL errors contain structured item_errors
                    // in extensions (from checkout-service CART_SYNC_VALIDATION_FAILED).
                    // If so, attach them to the matching cart item reference lines
                    // instead of throwing a generic error.
                    // ────────────────────────────────────────────────────────────────
                    const structuredItemErrors = this.extractItemErrorsFromGraphQLErrors(result.errors);
                    if (structuredItemErrors.length > 0) {
                        console.warn(
                            '[CartSync] Structured item errors extracted from GraphQL errors:',
                            structuredItemErrors
                        );

                        const errorsByReferenceId = new Map<string, CartItemReferenceError>();
                        structuredItemErrors.forEach(itemError => {
                            errorsByReferenceId.set(itemError.cart_item_reference_id, {
                                errorCode: itemError.error_code,
                                message: itemError.message,
                                sku: itemError.sku ?? undefined,
                            });
                        });

                        cartItemReferences.forEach(ref => {
                            const refError = errorsByReferenceId.get(ref.cartItemReferenceId);
                            if (refError) {
                                ref.error = refError;
                            }
                        });

                        // Do NOT throw — errors are attached to individual references
                        // and the sync can proceed with whatever data is available.
                    } else {
                        throw new Error(
                            `SynchronizeCart mutation failed: ${result.errors.map(e => e.message).join(', ')}`
                        );
                    }
                }

                const syncedCart = result.data?.synchronizeCart?.cart;
                if (!syncedCart) {
                    throw new Error('SynchronizeCart mutation returned no cart data');
                }

                // ════════════════════════════════════════════════════════════════════
                // STEP 2.5: Process per-item errors from server
                // ════════════════════════════════════════════════════════════════════
                const itemErrors = result.data?.synchronizeCart?.item_errors;
                if (itemErrors && itemErrors.length > 0) {
                    console.warn('[CartSync] Per-item errors received from server:', itemErrors);

                    // Build a map of referenceId -> error for quick lookup
                    const errorsByReferenceId = new Map<string, CartItemReferenceError>();
                    itemErrors.forEach(itemError => {
                        errorsByReferenceId.set(itemError.cart_item_reference_id, {
                            errorCode: itemError.error_code,
                            message: itemError.message,
                            sku: itemError.sku ?? undefined,
                        });
                    });

                    // Attach errors to matching references
                    cartItemReferences.forEach(ref => {
                        const refError = errorsByReferenceId.get(ref.cartItemReferenceId);
                        if (refError) {
                            ref.error = refError;
                        } else {
                            // Clear previous error if no longer present
                            ref.error = undefined;
                        }
                    });

                    // ────────────────────────────────────────────────────────────────
                    // GIFT ERROR AUTO-CLEANUP: Strip invalid gift actions from local
                    // references so they are not re-sent on the next sync cycle.
                    // Handles GIFT_NOT_SALABLE and GIFT_INSUFFICIENT_STOCK errors.
                    // ────────────────────────────────────────────────────────────────

                    /**
                        @deprecated don't handle this case
                        const giftErrorCodes = new Set(['GIFT_NOT_SALABLE', 'GIFT_INSUFFICIENT_STOCK']);
                        const giftErrors = itemErrors.filter(e => giftErrorCodes.has(e.error_code as string));
                        if (giftErrors.length > 0) {
                            const invalidGiftSkus = new Set(giftErrors.filter(e => e.sku).map(e => e.sku!));

                            if (invalidGiftSkus.size > 0) {
                                console.warn(
                                    '[CartSync] Auto-stripping invalid gift actions for SKUs:',
                                    Array.from(invalidGiftSkus)
                                );

                                cartItemReferences.forEach(ref => {
                                    if (!ref.logics) return;
                                    ref.logics = ref.logics.map(logic => ({
                                        ...logic,
                                        actions: (logic.actions ?? []).filter(action => {
                                            if (action.actionType !== 'free_gift') return true;
                                            const actionSku = action.actionProduct?.sku;
                                            if (actionSku && invalidGiftSkus.has(actionSku)) {
                                                console.warn(
                                                    `[CartSync] Stripped invalid gift action: SKU=${actionSku} from ref=${ref.cartItemReferenceId}`
                                                );
                                                return false;
                                            }
                                            return true;
                                        }),
                                    }));
                                });
                            }
                        } 
                     */
                } else {
                    // Clear all previous errors when no errors returned
                    cartItemReferences.forEach(ref => {
                        ref.error = undefined;
                    });
                }

                // ════════════════════════════════════════════════════════════════════
                // STEP 3: Collect cart update mutation
                // ════════════════════════════════════════════════════════════════════
                // Cast to Cart type (the mutation returns minimal cart data)
                mutationCollector.setCart(syncedCart as Cart);

                // ════════════════════════════════════════════════════════════════════
                // STEP 4: Update references with server cart item IDs
                // ════════════════════════════════════════════════════════════════════
                // Build map of SKU -> server cart item
                const serverCartItems = syncedCart.items ?? [];
                const skuToServerItem = new Map<string, { id: string; quantity: number }>();

                serverCartItems.forEach(item => {
                    if (!item) return;

                    // For configurable cart items, use configured_variant.sku as key
                    // For simple cart items, use product.sku
                    const configurableItem = item as { configured_variant?: { sku?: string } };
                    const itemSku = configurableItem?.configured_variant?.sku ?? item.product.sku;
                    if (itemSku) {
                        skuToServerItem.set(itemSku, {
                            id: item.id,
                            quantity: item.quantity,
                        });
                    }
                });

                // Update references with server-assigned IDs
                cartItemReferences.forEach(ref => {
                    // Use the same SKU resolution as convertReferenceToSyncItem
                    const cartItem = ref.cartItem;
                    const productSku = cartItem?.product?.sku;
                    const configurableCartItem = cartItem as ConfigurableCartItem | undefined;
                    const variantSku = configurableCartItem?.configured_variant?.sku;
                    // Prioritize ref.sku (variant SKU from server) over product.sku (parent for configurable)
                    let lookupSku = variantSku ?? ref.sku ?? productSku;

                    // Guard: If lookupSku equals parent SKU, use ref.sku instead
                    if (ref.parentProduct?.sku && lookupSku === ref.parentProduct.sku) {
                        console.warn('[CartSync] lookupSku equals parent SKU, using ref.sku instead', {
                            lookupSku,
                            refSku: ref.sku,
                            parentSku: ref.parentProduct.sku,
                            referenceId: ref.cartItemReferenceId,
                        });
                        lookupSku = ref.sku && ref.sku !== ref.parentProduct.sku ? ref.sku : lookupSku;
                    }

                    if (!lookupSku) return;

                    const serverItem = skuToServerItem.get(lookupSku);

                    if (serverItem && ref.isSelected) {
                        // Item exists on server - update IDs
                        const updatedRef: CartItemReference = {
                            ...ref,
                            cartId,
                            cartItemId: serverItem.id,
                            cartItem: ref.cartItem
                                ? {
                                      ...ref.cartItem,
                                      id: serverItem.id,
                                  }
                                : undefined,
                        };
                        mutationCollector.addReferenceUpdate(updatedRef);
                    } else if (!ref.isSelected && ref.cartItemId) {
                        // Item was unselected and should be removed - clear IDs
                        const updatedRef: CartItemReference = {
                            ...ref,
                            cartId: undefined,
                            cartItemId: undefined,
                            cartItem: ref.cartItem
                                ? {
                                      ...ref.cartItem,
                                      id: '',
                                  }
                                : undefined,
                        };
                        mutationCollector.addReferenceUpdate(updatedRef);
                    }
                });

                cartItemReferenceSynchronizationStore$.syncStatus.set('idle');
                return mutationCollector.getMutations();
            } catch (error) {
                cartItemReferenceSynchronizationStore$.syncStatus.set('error');
                console.error('[CartSync][SynchronizeCartAPI] Sync failed:', error);

                const errorMessage = error instanceof Error ? error.message : String(error);
                flashMessageAPI.addError(errorMessage, 'Có lỗi xảy ra');

                return {
                    ...mutationCollector.getMutations(),
                    error: error instanceof Error ? error : new Error(String(error)),
                };
            }
            // ════════════════════════════════════════════════════════════════════
            // CRITICAL FIX: Do NOT reset isCurrentlySyncing here!
            // It must stay true during applyStoreMutations (which runs outside
            // the semaphore). Previously, resetting here created a race window
            // where debounce timers could start a new sync while
            // applyStoreMutations was still modifying stores.
            // ════════════════════════════════════════════════════════════════════
        });

        // Apply mutations atomically — isCurrentlySyncing is still TRUE here
        try {
            if (mutations.error) {
                throw mutations.error;
            }

            // ════════════════════════════════════════════════════════════════════
            // CRITICAL: Call onBeforeApplyMutations BEFORE store writes.
            // This allows executeSyncToServer to update lastSyncedState while
            // isInternalUpdate is NOT yet set and isCurrentlySyncing is still true.
            // This eliminates the timing gap where onChange could see stale
            // lastSyncedState and trigger spurious re-syncs.
            // ════════════════════════════════════════════════════════════════════
            if (onBeforeApplyMutations) {
                onBeforeApplyMutations();
            }

            this.applyStoreMutations(mutations);
        } finally {
            // ════════════════════════════════════════════════════════════════════
            // CRITICAL FIX: Reset isCurrentlySyncing AFTER applyStoreMutations
            // completes. This ensures the flag stays true during the entire
            // sync + apply cycle, preventing concurrent sync starts.
            // ════════════════════════════════════════════════════════════════════
            CartItemReferenceSynchronizationService.isCurrentlySyncing = false;
        }
    }

    /**
     * ════════════════════════════════════════════════════════════════════════════
     * APPLY STORE MUTATIONS ATOMICALLY
     * ════════════════════════════════════════════════════════════════════════════
     * Applies all collected mutations to stores in atomic manner.
     * Called AFTER semaphore release to prevent race conditions.
     *
     * CRITICAL: This is the ONLY place where stores are mutated during sync.
     * All mutations are collected during execution and applied here atomically.
     *
     * ENHANCED: After cart update, syncs cart item IDs from server to references.
     * This ensures newly created items get their server-assigned IDs.
     *
     * @param mutations - Collected mutations from sync execution
     */
    private applyStoreMutations(mutations: StoreMutations): void {
        console.log('[CartSync] Applying store mutations atomically', {
            cartUpdate: !!mutations.cartToUpdate,
            referencesSet: !!mutations.cartItemReferencesToSet,
            referencesUpdated: mutations.cartItemReferencesToUpdate.length,
            referencesAdded: mutations.cartItemReferencesToAdd.length,
        });

        // ════════════════════════════════════════════════════════════════════
        // CRITICAL: Set internal update flag to prevent recursive sync triggers
        // ════════════════════════════════════════════════════════════════════
        // When we update the store after sync, the onChange listener will fire.
        // We need to skip sync for these internal updates to prevent loops.
        setInternalUpdate(true);

        try {
            // ════════════════════════════════════════════════════════════════
            // PHASE 0: Read current local state as AUTHORITATIVE source
            // ════════════════════════════════════════════════════════════════
            // Local state is the single source of truth for user-facing data
            // (which items exist, qty, isSelected). Server response should
            // only ENRICH local items (add IDs, prices) — never ADD/REMOVE items.
            //
            // Build a Set of locally-existing reference IDs. Any server reference
            // NOT in this set was deleted by the user during the sync round-trip
            // and must NOT be written back.
            const currentLocalItems = cartItemReferenceStoreAPI.state.getItems();
            const localIsEmpty = currentLocalItems.length === 0;
            const localReferenceIds = new Set(currentLocalItems.map(r => r.cartItemReferenceId));

            // ════════════════════════════════════════════════════════════════
            // PHASE 1: Update cart store (server prices, totals)
            // ════════════════════════════════════════════════════════════════
            // GUARD: Only update cart store if local state hasn't diverged.
            // If user deleted items during sync, server response contains stale
            // items — writing it to cart store would show wrong counts/prices.
            if (mutations.cartToUpdate) {
                if (localIsEmpty && (mutations.cartToUpdate.items?.length ?? 0) > 0) {
                    // LOCAL REFERENCES CLEARED: User removed ALL items during sync.
                    console.warn(
                        '[CartSync] Skipping cart store update — local references are empty but server has items.',
                        {
                            serverItemCount: mutations.cartToUpdate.items?.length ?? 0,
                            localItemCount: 0,
                        }
                    );
                } else if (!localIsEmpty && mutations.cartToUpdate.items) {
                    // ════════════════════════════════════════════════════════════
                    // PARTIAL DELETION GUARD: Filter cart store items to match
                    // local references. If user deleted 1 of 3 items during sync,
                    // server response still has 3 items. Writing all 3 to cart
                    // store would show wrong count until next sync completes.
                    // ════════════════════════════════════════════════════════════
                    const serverItemCount = mutations.cartToUpdate.items.length;

                    // Only filter if local has fewer items than server (user deleted during sync)
                    if (currentLocalItems.length < serverItemCount) {
                        console.warn(
                            '[CartSync] Partial deletion detected — local has fewer items than server response.',
                            {
                                serverItemCount,
                                localItemCount: currentLocalItems.length,
                            }
                        );
                        // Skip cart store update entirely — let the pending sync
                        // send the correct local state and get accurate response.
                    } else {
                        cartStoreAPI.actions.updateCartFromServerResponse(mutations.cartToUpdate, {
                            updateTimestamps: true,
                            clearIfEmpty: true,
                        });
                    }
                } else {
                    cartStoreAPI.actions.updateCartFromServerResponse(mutations.cartToUpdate, {
                        updateTimestamps: true,
                        clearIfEmpty: true,
                    });
                }
            }

            // ════════════════════════════════════════════════════════════════
            // PHASE 2: Sync cart item IDs from server to references
            // ════════════════════════════════════════════════════════════════
            if (mutations.cartToUpdate) {
                this.syncCartItemIdsFromServerToReferences(mutations);
            }

            // ════════════════════════════════════════════════════════════════
            // PHASE 3: Update cart item references
            // ════════════════════════════════════════════════════════════════
            // PRINCIPLE: Local state is authoritative for user-facing data.
            // Server response only ENRICHES existing local items (IDs, prices).
            // Server response must NEVER:
            //   - Add items the user has deleted
            //   - Override qty/isSelected the user has changed
            //   - Resurrect references that no longer exist locally
            if (localIsEmpty) {
                console.warn(
                    '[CartSync] Skipping reference mutations — local references are empty (user cleared cart).',
                    {
                        referencesToUpdate: mutations.cartItemReferencesToUpdate.length,
                        referencesToAdd: mutations.cartItemReferencesToAdd.length,
                        referencesToSet: mutations.cartItemReferencesToSet?.length ?? 0,
                    }
                );
            } else if (mutations.cartItemReferencesToSet) {
                // Bulk update — filter to only include locally-existing references
                const filteredRefs = mutations.cartItemReferencesToSet.filter(ref =>
                    localReferenceIds.has(ref.cartItemReferenceId)
                );
                if (filteredRefs.length < (mutations.cartItemReferencesToSet?.length ?? 0)) {
                    console.warn('[CartSync] Filtered out server references not in local state during bulk set', {
                        original: mutations.cartItemReferencesToSet?.length ?? 0,
                        filtered: filteredRefs.length,
                    });
                }
                cartItemReferenceStoreAPI.actions.setItems(filteredRefs);
            } else {
                const localMap = new Map(currentLocalItems.map(r => [r.cartItemReferenceId, r]));

                // Individual updates — ONLY for references that still exist locally
                mutations.cartItemReferencesToUpdate.forEach(serverRef => {
                    const localRef = localMap.get(serverRef.cartItemReferenceId);
                    if (localRef) {
                        // Merge: take server's IDs & prices, keep local qty & isSelected
                        const merged: CartItemReference = {
                            ...serverRef,
                            qty: localRef.qty,
                            isSelected: localRef.isSelected,
                        };
                        cartItemReferenceStoreAPI.actions.updateItem(merged);
                    }
                    // ════════════════════════════════════════════════════════
                    // CRITICAL FIX: Do NOT write serverRef if localRef is missing.
                    // Missing localRef means user deleted this item during sync.
                    // Writing it back would resurrect a deleted item.
                    // The pending sync will send the correct local state to server.
                    // ════════════════════════════════════════════════════════
                });

                // CRITICAL FIX: Do NOT blindly add server references.
                // Only add if the reference ID exists in local state (meaning
                // it was added locally but hasn't been synced yet).
                mutations.cartItemReferencesToAdd.forEach(ref => {
                    if (localReferenceIds.has(ref.cartItemReferenceId)) {
                        cartItemReferenceStoreAPI.actions.addItem(ref);
                    } else {
                        console.warn('[CartSync] Skipping addItem for non-local reference:', {
                            refId: ref.cartItemReferenceId,
                        });
                    }
                });
            }

            console.log('[CartSync] Store mutations applied successfully');
        } catch (error) {
            console.error('[CartSync] Failed to apply store mutations:', error);

            const errorMessage = error instanceof Error ? error.message : String(error);
            flashMessageAPI.addError(errorMessage, 'Có lỗi xảy ra');

            throw error;
        } finally {
            // ════════════════════════════════════════════════════════════════
            // CRITICAL: Reset internal update flag
            // ════════════════════════════════════════════════════════════════
            setInternalUpdate(false);
        }
    }

    /**
     * ════════════════════════════════════════════════════════════════════════════
     * SYNC CART ITEM IDS FROM SERVER TO REFERENCES
     * ════════════════════════════════════════════════════════════════════════════
     * After cart update from server, sync cart item IDs back to references.
     * This ensures newly created items have their server-assigned IDs.
     *
     * ALGORITHM:
     * 1. Get cart items from server response (source of truth)
     * 2. For each reference (from all mutation arrays)
     * 3. Match by SKU (simple) or parent_sku+variant_sku (configurable)
     * 4. Update reference with cart item ID and cart ID from server
     *
     * @param mutations - Mutations containing cart response and references to update
     */
    private syncCartItemIdsFromServerToReferences(mutations: StoreMutations): void {
        const cart = mutations.cartToUpdate;
        if (!cart || !cart.items || cart.items.length === 0) {
            return;
        }

        // Build map of SKU -> cart item from server response
        const skuToCartItem = new Map<string, CartItemInterface>();

        cart.items.forEach(item => {
            if (!item || !item.product || !item.product.sku) {
                return;
            }

            let skuKey: string;

            // Check if configurable product
            const configurableItem = item as ConfigurableCartItem;
            if (configurableItem.configured_variant?.sku) {
                // Configurable: use parent_sku|variant_sku as key
                const parentSku = item.product.sku;
                const variantSku = configurableItem.configured_variant.sku;
                skuKey = `${parentSku}|${variantSku}`;
            } else {
                // Simple: use SKU as key
                skuKey = item.product.sku;
            }

            skuToCartItem.set(skuKey, item);
        });

        // Update references with cart item IDs from server
        const allReferences = [
            ...mutations.cartItemReferencesToUpdate,
            ...mutations.cartItemReferencesToAdd,
            ...(mutations.cartItemReferencesToSet || []),
        ];

        let updatedCount = 0;

        allReferences.forEach(ref => {
            if (!ref.cartItem) {
                return;
            }

            // Determine SKU key for this reference
            let skuKey: string;

            if (ref.parentProduct) {
                // Configurable product
                const parentSku = ref.parentProduct.sku;
                const variantSku =
                    (ref.cartItem as unknown as ConfigurableCartItem)?.configured_variant?.sku ?? ref.sku;

                if (!parentSku || !variantSku) {
                    return;
                }

                // Guard: If variantSku equals parentSku, use ref.sku as corrected variant SKU
                if (variantSku === parentSku) {
                    console.warn('[CartSync] syncCartItemIds: variantSku equals parentSku, using ref.sku instead', {
                        parentSku,
                        variantSku,
                        refSku: ref.sku,
                        referenceId: ref.cartItemReferenceId,
                    });
                    const correctedVariantSku = ref.sku && ref.sku !== parentSku ? ref.sku : undefined;
                    if (!correctedVariantSku) {
                        return; // Cannot determine variant SKU — skip
                    }
                    skuKey = `${parentSku}|${correctedVariantSku}`;
                } else {
                    skuKey = `${parentSku}|${variantSku}`;
                }
            } else {
                // Simple product
                const sku = ref.cartItem?.product?.sku;
                if (!sku) {
                    return;
                }
                skuKey = sku;
            }

            // Find matching cart item from server
            const serverCartItem = skuToCartItem.get(skuKey);

            // ════════════════════════════════════════════════════════════════════
            // CRITICAL: Handle unselected items (quantity = 0)
            // ════════════════════════════════════════════════════════════════════
            // If reference has cart item ID but server doesn't have matching item,
            // it means:
            // 1. Item was unselected (isSelected = false)
            // 2. Sync sent quantity = 0
            // 3. Server DELETED the cart item
            // 4. We must RESET all IDs in reference
            if (!serverCartItem || !serverCartItem.id) {
                // Check if reference previously had cart item ID
                const hadCartItemId = ref.cartItemId && ref.cartItemId !== '';

                if (hadCartItemId) {
                    // Item was removed from server (quantity = 0)
                    // Reset ALL IDs

                    ref.cartId = undefined;
                    ref.cartItemId = undefined;

                    // Reset cartItem.id as well (use empty string for type compliance)
                    if (ref.cartItem) {
                        ref.cartItem.id = '';
                    }

                    updatedCount++;
                }

                return; // Skip to next reference
            }

            // ════════════════════════════════════════════════════════════════════
            // Normal case: Update reference with server IDs
            // ════════════════════════════════════════════════════════════════════
            const oldCartItemId = ref.cartItemId;
            const oldCartId = ref.cartId;

            ref.cartId = mutations.cartId;
            ref.cartItemId = serverCartItem.id;

            // Update cartItem.id as well
            if (ref.cartItem) {
                ref.cartItem.id = serverCartItem.id;
            }

            if (oldCartItemId !== serverCartItem.id || oldCartId !== mutations.cartId) {
                updatedCount++;
            }
        });

        console.log(`[CartSync] Synced ${updatedCount} references with server cart item IDs`);
    }

    /**
     * Check if sync is currently in progress
     * Used by store to prevent recursive sync triggers
     */
    static isInProgress(): boolean {
        return CartItemReferenceSynchronizationService.isCurrentlySyncing;
    }

    /**
     * ========================================================================
     * CLEAN STALE CART ITEM IDS - PURE FUNCTION
     * ========================================================================
     * Separate mutation logic from filter predicate to follow functional programming principles
     *
     * For selected references with stale cartItemIds (cartItemId exists but not found in currentCartItems):
     * - Clear cartItemId and cartId to treat as new items
     * - Keep cartItem to preserve product data
     * - This allows them to be re-added to cart properly
     *
     * IMPORTANT: This function returns NEW reference objects (immutable approach)
     * Original references are never mutated
     *
     * @param references - References to check and clean
     * @param currentCartItemIds - Set of valid cart item IDs from current cart
     * @returns New array with cleaned references (mutation-free)
     */
    private cleanStaleCartItemIds(
        references: CartItemReference[],
        currentCartItemIds: Set<string>
    ): CartItemReference[] {
        return references.map(ref => {
            // Only process selected items with cartItem
            if (!ref.isSelected || !ref.cartItem) {
                return ref;
            }

            // Extract valid cart item ID from reference
            const refCartItemId = ref.cartItem.id;
            const hasValidId = !!refCartItemId && !refCartItemId.startsWith('new-');

            // If no valid ID, keep original reference
            if (!hasValidId) {
                return ref;
            }

            // Check if cartItemId exists in current cart
            const hasValidCartItem = currentCartItemIds.has(refCartItemId);

            // If all cartItemIds are stale, return cleaned reference
            if (!hasValidCartItem) {
                // Return NEW reference object with cleaned IDs (immutable approach)
                // Keep cartItem to preserve product data (SKU, name, price, etc.)
                // Reset ALL cart-related IDs: cartItemId, cartId, and cartItem.id
                return {
                    ...ref,
                    cartItemId: undefined, // Reset - will be assigned after re-adding to cart
                    cartId: undefined, // Reset - will be assigned after re-adding to cart
                    // CRITICAL: Reset cartItem.id as well - must clear ALL cart-related IDs
                    // Use empty string '' instead of undefined to comply with GraphQL type string
                    cartItem: ref.cartItem
                        ? {
                              ...ref.cartItem,
                              id: 'debug-stale', // Reset cart item id - stale ID no longer valid
                          }
                        : undefined,
                    // cartItem data preserved (SKU, name, price, image) - contains valuable product data
                };
            }

            // CartItemId is valid, keep original reference
            return ref;
        });
    }

    /**
     * Sync cart item references to Magento
     *
     * NOW USES: SynchronizeCart API (single API call)
     * This replaces the old multi-request approach with a single atomic mutation.
     *
     * BENEFITS:
     * - Single network request instead of multiple
     * - Server-side validation and calculation
     * - Atomic operation - all or nothing
     * - Reduced complexity and race conditions
     *
     * CRITICAL: Uses Semaphore to ensure sequential execution
     * - Only one sync operation runs at a time (mutex with 1 permit)
     * - Concurrent calls are automatically queued by semaphore
     * - Prevents race conditions and data inconsistency
     */
    async sync(
        cartId: string,
        cartItemReferences: CartItemReference[],
        onBeforeApplyMutations?: () => void
    ): Promise<void> {
        // Delegate to new SynchronizeCart API
        return this.syncWithSynchronizeCartAPI(cartId, cartItemReferences, onBeforeApplyMutations);
    }

    /**
     * ════════════════════════════════════════════════════════════════════════════
     * LEGACY SYNC METHOD - Kept for reference/rollback
     * ════════════════════════════════════════════════════════════════════════════
     * This method uses multiple API calls (UpdateCartItems, AddSimpleProductsToCart, etc.)
     * Use syncWithSynchronizeCartAPI for the new single-API approach.
     *
     * @deprecated Use sync() which now delegates to syncWithSynchronizeCartAPI
     */
    async syncLegacy(cartId: string, cartItemReferences: CartItemReference[]): Promise<void> {
        // ════════════════════════════════════════════════════════════════════════
        // PHASE 1: FETCH CART ITEM IDs FROM SERVER FOR RECONCILIATION
        // ════════════════════════════════════════════════════════════════════════
        // CRITICAL: This API (CartItemsOnly) is ONLY for reconciliation purposes
        // - Returns: Cart item IDs and SKUs (minimal data for comparison)
        // - Purpose: Compare server items with local references
        // - NOT for updating cartStore (that's handled by another job)
        //
        // WHY FETCH FROM SERVER:
        // - Server is source of truth for what items exist
        // - Detect items added/removed from other devices
        // - Ensure local references match server state

        console.log('[CartSync] Fetching cart item IDs from server for reconciliation', { cartId });

        if (!cartId) {
            throw new Error('[CartSync] Cart ID is required for synchronization');
        }

        const client = await this.getClient();
        if (!client) {
            throw new Error('[CartSync] Apollo client not initialized');
        }

        // ════════════════════════════════════════════════════════════════════════
        // PHASE 2: EXECUTE SYNC AND COLLECT MUTATIONS (Inside Semaphore)
        // ════════════════════════════════════════════════════════════════════════
        // Use semaphore.execute() for automatic acquire/release with proper error handling
        // Execution is PURE - no store mutations, only collection
        const mutations = await CartItemReferenceSynchronizationService.syncSemaphore.execute(async () => {
            // CRITICAL: Set flag to prevent recursive sync triggers
            CartItemReferenceSynchronizationService.isCurrentlySyncing = true;

            let currentCartItemsSnapshot: CartItemInterface[] = [];

            try {
                const { data } = await client.query({
                    query: CartItemsOnlyDocument,
                    variables: { cartId },
                    fetchPolicy: 'network-only', // Always fetch from network, not cache
                });

                if (data?.cartItemsOnly?.items) {
                    currentCartItemsSnapshot = data.cartItemsOnly.items.filter(
                        (item: CartItemInterface | null): item is CartItemInterface => item != null
                    );
                    console.log('[CartSync] Fetched cart item IDs from server for reconciliation', {
                        itemCount: currentCartItemsSnapshot.length,
                        items: currentCartItemsSnapshot.map(item => ({
                            id: item.id,
                            sku: item.product?.sku,
                        })),
                    });
                } else {
                    console.log('[CartSync] No cart items returned from server');
                }
            } catch (fetchError) {
                console.error('[CartSync] Failed to fetch cart items from server:', fetchError);

                const errorMessage = fetchError instanceof Error ? fetchError.message : String(fetchError);
                flashMessageAPI.addWarning(errorMessage, 'Có lỗi xảy ra');

                // Fallback to local store if server fetch fails
                currentCartItemsSnapshot = [...(cartStoreAPI.state.getCartItems() ?? [])];
                console.warn('[CartSync] Using fallback cart items from local store for reconciliation', {
                    itemCount: currentCartItemsSnapshot.length,
                });
            }

            // Snapshot 2: All cart item references (for migration check)
            const allReferencesSnapshot = [...(cartItemReferenceStoreAPI.state.getItems() ?? [])];

            console.log('[CartSync] Starting sync operation with server reconciliation', {
                cartId,
                cartItemReferences,
                allReferencesSnapshot,
                serverCartItemsCount: currentCartItemsSnapshot.length,
            });
            try {
                return await this.executeSyncOperation(
                    cartId,
                    cartItemReferences,
                    currentCartItemsSnapshot,
                    allReferencesSnapshot
                );
            } finally {
                // CRITICAL: Clear flag to allow future syncs
                CartItemReferenceSynchronizationService.isCurrentlySyncing = false;
            }
        });

        // ════════════════════════════════════════════════════════════════════════
        // PHASE 3: APPLY MUTATIONS ATOMICALLY (Outside Semaphore)
        // ════════════════════════════════════════════════════════════════════════
        // Apply all collected mutations to stores atomically
        // This happens AFTER semaphore release to prevent race conditions
        if (mutations.error) {
            throw mutations.error;
        }

        this.applyStoreMutations(mutations);
    }

    /**
     * Execute the actual sync operation
     * Protected by semaphore to ensure sequential execution
     *
     * CRITICAL: Pure function - NO store mutations
     * - No store access during execution (uses snapshots)
     * - Collects all mutations instead of applying them
     * - Returns mutations for atomic application later
     * - Ensures data consistency throughout operation
     */
    private async executeSyncOperation(
        cartId: string,
        cartItemReferences: CartItemReference[],
        currentCartItemsSnapshot: CartItemInterface[],
        allReferencesSnapshot: CartItemReference[]
    ): Promise<StoreMutations> {
        // ════════════════════════════════════════════════════════════════════════
        // INITIALIZE MUTATION COLLECTOR
        // ════════════════════════════════════════════════════════════════════════
        const mutations = new MutationCollector(cartId);
        console.log('[CartSync] Requested sync operation', {
            cartId,
            cartItemReferences,
            currentCartItemsSnapshot: currentCartItemsSnapshot,
            allReferencesSnapshot: allReferencesSnapshot,
        });
        if (!cartId) {
            cartItemReferenceSynchronizationStore$.syncStatus.set('idle');
            return mutations.getMutations();
        }

        if (!cartItemReferences || cartItemReferences.length === 0) {
            cartItemReferenceSynchronizationStore$.syncStatus.set('idle');
            return mutations.getMutations();
        }

        cartItemReferenceSynchronizationStore$.syncStatus.set('loading');

        try {
            // Use snapshot data instead of accessing store
            const currentCartItems = currentCartItemsSnapshot;
            // Create a Set of valid cart item IDs from current cart for efficient lookup
            const currentCartItemIds = new Set(
                currentCartItems
                    .filter(
                        item =>
                            item.id && !item.id.toString().startsWith('new-') && (item.prices?.row_total.value ?? 0) > 0
                    )
                    .map(item => item.id)
                    .filter((id): id is string => !!id && !id.startsWith('new-'))
            );

            // CRITICAL: Filter references to only include those belonging to current cart
            // CART MIGRATION SUPPORT: Detect and handle cartId changes (login scenarios)
            // This prevents cross-cart contamination and sync errors
            // UPDATED: Include unselected items with cartId and cartItemId for removal synchronization

            // ========================================================================
            // PHASE 1: CATEGORIZE REFERENCES (Pure filter - NO mutation)
            // ========================================================================
            const referencesToMigrate: CartItemReference[] = [];
            const referencesToInclude: CartItemReference[] = [];

            const filteredReferences = cartItemReferences.filter(ref => {
                const isSelected = ref.isSelected;

                // ============================================================================
                // CART ID MISMATCH DETECTION - CRITICAL FOR LOGIN SCENARIOS
                // ============================================================================
                if (ref.cartId && ref.cartId !== cartId) {
                    if (isSelected) {
                        // CASE: Selected item with cartId mismatch → MIGRATE to new cart
                        referencesToMigrate.push(ref);
                        return true; // Include for processing
                    } else {
                        // CASE: Unselected item with cartId mismatch → FILTER OUT
                        return false; // Exclude
                    }
                }

                // ============================================================================
                // NORMAL FILTERING LOGIC
                // ============================================================================

                // CASE 1: Unselected items with cartId and cartItemId - must be removed from server
                // These items need to be synced with quantity = 0
                if (!isSelected && ref.cartId && ref.cartItem) {
                    const hasValidCartItemId = ref.cartItem.id && !ref.cartItem.id.startsWith('new-');

                    if (hasValidCartItemId && ref.cartId === cartId) {
                        referencesToInclude.push(ref);
                        return true;
                    }
                }

                // If not selected and doesn't have cartId/cartItemId, exclude
                if (!isSelected) {
                    return false;
                }

                // CASE 2: Reference without cartId - new items to be added
                // These should be included for sync (they will be added to current cart)
                if (!ref.cartId && isSelected) {
                    referencesToInclude.push(ref);
                    return true;
                }

                // CASE 3: Reference with cartId - existing items
                // Must match current cartId (already checked above, so cartId matches here)
                referencesToInclude.push(ref);
                return true;
            });
            // ========================================================================
            // PHASE 2: MIGRATION - Update references that need migration
            // CRITICAL: Only update cartId, cartItemId, and cartItem.id
            // DO NOT change data structure!
            // ========================================================================
            if (referencesToMigrate.length > 0) {
                // Update references in place (mutable approach for performance)
                referencesToMigrate.forEach(ref => {
                    // CRITICAL: Reset IDs for migration - ONLY these fields!
                    ref.cartId = undefined; // Will be set after sync
                    ref.cartItemId = undefined; // Will be assigned from server

                    // Reset cartItem.id if exists
                    // Use empty string '' to comply with GraphQL type string (not nullable)
                    if (ref.cartItem) {
                        ref.cartItem.id = '';
                    }

                    // Keep ALL other fields unchanged:
                    // - isSelected, qty, productId
                    // - logics (promotions, gifts)
                    // - cartItem data (SKU, name, price, image)
                });

                // ========================================================================
                // PHASE 3: COLLECT MIGRATION MUTATIONS (No immediate store update)
                // ========================================================================
                // Collect mutations instead of applying them directly
                // Mutations will be applied atomically after sync completes
                if (allReferencesSnapshot && allReferencesSnapshot.length > 0) {
                    referencesToMigrate.forEach(migratedRef => {
                        const index = allReferencesSnapshot.findIndex(
                            r => r.cartItemReferenceId === migratedRef.cartItemReferenceId
                        );

                        if (index >= 0) {
                            // Collect mutation - will be applied later
                            mutations.addReferenceUpdate(migratedRef);
                        }
                    });
                }
            }

            // CRITICAL: Clean stale cartItemIds from filtered references
            // This step is separated from filter logic to follow functional programming principles
            // Returns NEW reference objects with cleaned data (immutable approach)
            const cleanedReferences = this.cleanStaleCartItemIds(filteredReferences, currentCartItemIds);

            if (cleanedReferences.length === 0) {
                console.log(
                    '[CartSync] No valid references to sync after cleaning. Ensuring cart items have references.'
                );
                // CRITICAL: Ensure all cart items have references, even when no selected references exist
                // This creates references for orphaned cart items instead of deleting them
                await this.ensureReferencesForAllCartItems(currentCartItems, [], cartId, mutations);

                cartItemReferenceSynchronizationStore$.syncStatus.set('idle');
                return mutations.getMutations();
            }

            // Step 1: Validate references before sync
            const validation = this.reconciliationService.validateReferencesForSync(cleanedReferences);

            if (validation.errors.length > 0) {
                throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
            }

            // Step 2: Get only selected references for process logics (for tracking)
            const selectedReferencesToSync = getReferencesToSync(cleanedReferences);

            // Step 3: Build sync operations from ALL cleaned references (selected + unselected for removal)
            const {
                updateItems,
                simpleProductItems,
                configurableProductItems,
                referencesBySku,
                skuToCartItemId,
                orphanedCartItemIds,
            } = this.buildSyncOperations(
                cleanedReferences, // Pass cleaned refs instead of filtered
                currentCartItems
            );

            console.log('[CartSync] Built sync operations', {
                updateItems,
                simpleProductItems,
                configurableProductItems,
                referencesBySku,
                skuToCartItemId,
                orphanedCartItemIds,
            });

            // ════════════════════════════════════════════════════════════════════════════
            // Step 3.5: IMMEDIATELY remove orphaned items from cartStore
            // ════════════════════════════════════════════════════════════════════════════
            // CRITICAL: Remove orphaned items from local cartStore BEFORE server sync
            // This provides instant UI feedback - items disappear immediately
            // Server response will confirm the deletion
            //
            // WHY BEFORE SERVER SYNC:
            // 1. Instant UI update - better UX
            // 2. Prevents race conditions if user interacts during sync
            // 3. Server response will overwrite with correct state anyway
            // Track latest cart data from server responses
            // Note: Orphaned items already removed from cartStore in buildSyncOperations
            let latestCartFromServer = null;

            // Step 4: Execute update operations and capture cart response
            if (updateItems.length > 0) {
                const cartResponse = await this.executeUpdateItems(cartId, updateItems);
                if (cartResponse) {
                    latestCartFromServer = cartResponse;
                }
            }

            // Step 5: Execute add operations for simple products and capture cart response
            if (simpleProductItems.length > 0) {
                const { skuToNewCartItemId, cartResponse } = await this.executeAddItems(cartId, simpleProductItems);

                // Update skuToCartItemId map with newly created cart item IDs
                skuToNewCartItemId.forEach((cartItemId, sku) => {
                    skuToCartItemId.set(sku, cartItemId);
                });

                if (cartResponse) {
                    latestCartFromServer = cartResponse;
                }
            }

            // Step 6: Execute add operations for configurable products and capture cart response
            if (configurableProductItems.length > 0) {
                const { skuToNewCartItemId, cartResponse } = await this.executeAddConfigurableItems(
                    cartId,
                    configurableProductItems
                );

                // Update skuToCartItemId map with newly created cart item IDs
                skuToNewCartItemId.forEach((cartItemId, skuKey) => {
                    skuToCartItemId.set(skuKey, cartItemId);
                });

                if (cartResponse) {
                    latestCartFromServer = cartResponse;
                }
            }

            // Step 7: Update cart_item_id for ALL references in the same SKU group
            // This is CRITICAL to prevent duplicate cart items
            // Collect mutations instead of applying them directly
            await this.updateReferencesWithCartItemIds(
                referencesBySku,
                skuToCartItemId,
                cartId, // Pass cartId as parameter
                allReferencesSnapshot, // Pass snapshot instead of accessing store
                mutations // Pass mutation collector
            );

            // Step 8: Ensure all cart items have corresponding references
            // If a cart item exists in cartStore without a reference, CREATE a reference (don't delete the item)
            // This respects the principle: cartStore is only modified by server
            // Collect mutations instead of applying them directly
            await this.ensureReferencesForAllCartItems(
                currentCartItems,
                filteredReferences,
                cartId, // Pass cartId as parameter
                mutations // Pass mutation collector
            );

            // Step 9: COLLECT cart update mutation (No immediate store update)
            // Collect cart data from server to be applied atomically later
            //
            // ════════════════════════════════════════════════════════════════
            // CRITICAL: Cart store update data source
            // ════════════════════════════════════════════════════════════════
            // latestCartFromServer comes from MUTATION RESPONSES (full cart data):
            // - UpdateCartItems mutation response
            // - AddSimpleProductsToCart mutation response
            // - AddConfigurableProductsToCart mutation response
            // - AddGiftsToCart mutation response
            //
            // This is NOT from CartItemsOnly API (which has minimal data).
            // CartItemsOnly is ONLY used for reconciliation (comparing IDs).
            //
            // DO NOT use CartItemsOnly data to update cartStore!
            // It will break the cart data structure (missing prices, totals, etc.)
            // ════════════════════════════════════════════════════════════════
            if (latestCartFromServer) {
                // Collect mutation - will be applied later
                // This will handle ALL cart-related updates:
                // - Items update (add/remove/update quantities)
                // - Cart totals and prices
                // - Discounts and promotions
                // - Shipping addresses
                // - Cart metadata
                // - Timestamps (lastFetchedAt, lastSyncedAt)
                mutations.setCart(latestCartFromServer);
            }

            // Step 10: Process gifts from references with gift logics
            // After add/update operations, check if any references have gift actions
            // and add them to cart using AddGiftsToCart mutation
            // Collect cart update mutation from gift processing
            await this.processGiftsForReferences(cartId, cleanedReferences, mutations);

            // Track only selected references for process logics
            cartItemReferenceSelectedToProcessLogicsStore$.currentData.set(selectedReferencesToSync);

            // ════════════════════════════════════════════════════════════════════════
            // SUCCESS: Return collected mutations
            // ════════════════════════════════════════════════════════════════════════
            cartItemReferenceSynchronizationStore$.syncStatus.set('idle');
            console.log('[CartSync] Sync execution completed successfully, returning mutations');
            return mutations.getMutations();
        } catch (catchError) {
            // ════════════════════════════════════════════════════════════════════════
            // ERROR: Return mutations with error
            // ════════════════════════════════════════════════════════════════════════
            cartItemReferenceSynchronizationStore$.syncStatus.set('error');
            console.error('[CartSync] Sync execution failed:', catchError);

            const errorMessage = catchError instanceof Error ? catchError.message : String(catchError);
            flashMessageAPI.addError(errorMessage, 'Có lỗi xảy ra');

            return {
                ...mutations.getMutations(),
                error: catchError instanceof Error ? catchError : new Error(String(catchError)),
            };
        } finally {
            cartItemReferenceSynchronizationStore$.syncStatus.set('idle');
        }
    }

    /**
     * Build sync operations from references
     * Separates items to update vs items to add
     * CRITICAL LOGIC:
     * 1. Group references by SKU (or parent_sku+variant_sku for configurable)
     * 2. For each SKU group:
     *    - If any reference has cart_item_id → Update that cart_item_id with total quantity
     *    - If no reference has cart_item_id → Create new cart item
     * 3. After sync, update cart_item_id for ALL references in the same SKU group
     *
     * This prevents duplicate cart items when multiple references share the same SKU.
     */
    private buildSyncOperations(referencesToSync: CartItemReference[], currentCartItems: CartItemInterface[]) {
        console.log('[CartSync][BuildSyncOps] Starting buildSyncOperations', {
            referencesToSyncCount: referencesToSync.length,
            currentCartItemsCount: currentCartItems.length,
        });

        // Group references by SKU (or parent_sku+variant_sku for configurable)
        // Key: SKU or "parent_sku|variant_sku"
        // Value: Array of references with that SKU
        const referencesBySku = new Map<string, CartItemReference[]>();

        // CRITICAL FIX: Group ALL references (selected + unselected) by SKU
        // This ensures that if all references for a SKU are unselected,
        // we still process that SKU group and set quantity = 0
        referencesToSync.forEach(ref => {
            let skuKey: string;

            if (ref.parentProduct) {
                // Configurable product: use parent_sku|variant_sku as key
                const parentSku = ref.parentProduct.sku;
                const variantSku = (ref.cartItem as ConfigurableCartItem)?.configured_variant?.sku;
                if (!parentSku || !variantSku) {
                    console.warn('[CartSync][BuildSyncOps] Skipping configurable product with missing SKU', {
                        cartItemReferenceId: ref.cartItemReferenceId,
                        parentSku,
                        variantSku,
                    });
                    return;
                }
                skuKey = `${parentSku}|${variantSku}`;
            } else {
                // Simple product: use SKU as key
                const sku = ref.cartItem?.product?.sku;
                if (!sku) {
                    console.warn('[CartSync][BuildSyncOps] Skipping simple product with missing SKU', {
                        cartItemReferenceId: ref.cartItemReferenceId,
                        productId: ref.productId,
                    });
                    return;
                }
                skuKey = sku;
            }

            const existing = referencesBySku.get(skuKey) || [];
            existing.push(ref);
            referencesBySku.set(skuKey, existing);
        });

        console.log('[CartSync][BuildSyncOps] Grouped references by SKU', {
            uniqueSkuCount: referencesBySku.size,
            skuKeys: Array.from(referencesBySku.keys()),
        });

        // Create set of valid cart item IDs from current cart for validation
        const currentCartItemIds = new Set(
            currentCartItems
                .map(item => (item.id && !item.id.toString().startsWith('new-') ? +item.id : null))
                .filter((id): id is number => id !== null)
        );

        // ════════════════════════════════════════════════════════════════════════════
        // CRITICAL: Build set of cart item IDs that have local references
        // ════════════════════════════════════════════════════════════════════════════
        // Collect all cartItemIds from local references
        // This will be used to identify orphaned server items
        const referencedCartItemIds = new Set<number>();

        referencesToSync.forEach(ref => {
            // Collect cartItemId from reference
            if (ref.cartItemId && !ref.cartItemId.startsWith('new-')) {
                const cartItemId = +ref.cartItemId;
                if (cartItemId > 0) {
                    referencedCartItemIds.add(cartItemId);
                }
            }

            // Also collect from cartItem
            if (ref.cartItem?.id && !ref.cartItem.id.startsWith('new-')) {
                const cartItemId = +ref.cartItem.id;
                if (cartItemId > 0) {
                    referencedCartItemIds.add(cartItemId);
                }
            }

            // Ensure all references have default values for missing data
            if (!ref.qty || ref.qty < 0) {
                ref.qty = 0;
            }
            if (!ref.logics) {
                ref.logics = [];
            }
        });

        // Maps for sync operations
        const updateItemsMap = new Map<number, number>(); // cart_item_id → quantity
        const simpleProductItemsMap = new Map<string, number>(); // sku → quantity
        const configurableProductItemsMap = new Map<
            string,
            { parent_sku: string; variant_sku: string; quantity: number }
        >(); // "parent_sku|variant_sku" → data

        // Track SKU to cart_item_id mapping for updating references after sync
        const skuToCartItemId = new Map<string, number>(); // SKU key → cart_item_id

        // ════════════════════════════════════════════════════════════════════════════
        // PHASE 1: Process each SKU group from LOCAL REFERENCES
        // ════════════════════════════════════════════════════════════════════════════
        referencesBySku.forEach((references, skuKey) => {
            // CRITICAL: Calculate total quantity ONLY from SELECTED references
            // If all references are unselected, totalQuantity will be 0
            // This will trigger removal of the cart item
            const totalQuantity = references.reduce((sum, ref) => {
                // Only count selected references
                if (!ref.isSelected) {
                    return sum;
                }
                return sum + ref.qty;
            }, 0);

            // Find if any reference in this group has cart_item_id
            let existingCartItemId: number | null = null;
            for (const ref of references) {
                if (
                    ref.cartItem?.id &&
                    !ref.cartItem.id.startsWith('new-') &&
                    ((ref.cartItem?.prices as CartItemPrices)?.row_total?.value ?? 0) > 0
                ) {
                    const cartItemId = +ref.cartItem.id;
                    if (currentCartItemIds.has(cartItemId)) {
                        existingCartItemId = cartItemId;
                        break;
                    }
                }
            }

            const isConfigurable = skuKey.includes('|');

            if (existingCartItemId !== null) {
                // CASE 1: SKU group has existing cart_item_id → Update with EXACT quantity
                // This will replace server quantity with local quantity
                // If totalQuantity = 0 (all unselected), this will REMOVE the cart item
                updateItemsMap.set(existingCartItemId, totalQuantity);
                skuToCartItemId.set(skuKey, existingCartItemId);
            } else {
                // CASE 2: SKU group has no cart_item_id → Create new ONLY if quantity > 0
                // If all references are unselected, totalQuantity = 0, skip creation
                if (totalQuantity > 0) {
                    if (isConfigurable) {
                        // Configurable product
                        const [parentSku, variantSku] = skuKey.split('|');
                        configurableProductItemsMap.set(skuKey, {
                            parent_sku: parentSku,
                            variant_sku: variantSku,
                            quantity: totalQuantity,
                        });
                    } else {
                        // Simple product
                        simpleProductItemsMap.set(skuKey, totalQuantity);
                    }
                }
            }
        });

        // ════════════════════════════════════════════════════════════════════════════
        // PHASE 2: Identify ORPHANED SERVER ITEMS (exist on server but not in local)
        // ════════════════════════════════════════════════════════════════════════════
        // Compare server cart items with local references using cartItemId
        // If server item ID is NOT in referencedCartItemIds → Mark for deletion
        //
        // CRITICAL: This ensures that items deleted locally are removed from server
        // Comparison is based on cartId + cartItemId, not SKU
        //
        // Track orphaned item IDs for immediate cartStore cleanup
        const orphanedCartItemIds: number[] = [];

        currentCartItems.forEach(serverItem => {
            if (!serverItem || !serverItem.id || serverItem.id.startsWith('new-')) {
                return;
            }

            // Skip gift items (price = 0) - they are managed separately
            const itemPrice = serverItem.prices?.price?.value ?? 0;
            if (itemPrice === 0) {
                return;
            }

            const cartItemId = +serverItem.id;

            if (cartItemId > 0) {
                // Check if this cart item ID has a local reference
                const hasLocalReference = referencedCartItemIds.has(cartItemId);

                if (!hasLocalReference) {
                    // ORPHANED ITEM: Exists on server but NOT in local references
                    // → Mark for deletion by setting quantity = 0
                    console.log('[CartSync][BuildSyncOps] Found orphaned server item (no local reference)', {
                        cartItemId,
                        sku: serverItem.product?.sku,
                        serverQuantity: (serverItem as SimpleCartItem).quantity,
                        action: 'DELETE (quantity = 0)',
                        reason: 'cartItemId not found in local references',
                    });

                    // Add to updateItemsMap with quantity = 0 to trigger deletion
                    // If this cartItemId already exists in map, it will be overwritten
                    // (which is correct - local state takes precedence)
                    if (!updateItemsMap.has(cartItemId)) {
                        updateItemsMap.set(cartItemId, 0);
                        // Also track for immediate cartStore cleanup
                        orphanedCartItemIds.push(cartItemId);
                    }
                }
            }
        });

        // ════════════════════════════════════════════════════════════════════════════
        // IMMEDIATE CLEANUP: Remove orphaned items from cartStore (LOCAL STATE)
        // ════════════════════════════════════════════════════════════════════════════
        // Before sending server requests, immediately remove orphaned items from local store
        // This ensures UI reflects the deletion immediately without waiting for server response
        if (orphanedCartItemIds.length > 0) {
            console.log('[CartSync][BuildSyncOps] Immediately removing orphaned items from cartStore', {
                orphanedCount: orphanedCartItemIds.length,
                orphanedIds: orphanedCartItemIds,
            });

            orphanedCartItemIds.forEach(cartItemId => {
                try {
                    cartStoreAPI.actions.removeItem(String(cartItemId));
                    console.log('[CartSync][BuildSyncOps] Removed orphaned item from cartStore', {
                        cartItemId,
                    });
                } catch (error) {
                    console.error('[CartSync][BuildSyncOps] Failed to remove orphaned item from cartStore', {
                        cartItemId,
                        error,
                    });
                }
            });
        }

        // Convert maps to arrays
        // Ensure all quantities are non-negative (default to 0 if missing)
        const updateItems = Array.from(updateItemsMap.entries()).map(([cart_item_id, quantity]) => ({
            cart_item_id,
            quantity: quantity >= 0 ? quantity : 0,
        }));

        const simpleProductItems = Array.from(simpleProductItemsMap.entries()).map(([sku, quantity]) => ({
            data: {
                quantity: quantity > 0 ? quantity : 0,
                sku: sku || '',
            },
        }));

        const configurableProductItems = Array.from(configurableProductItemsMap.values()).map(
            ({ parent_sku, variant_sku, quantity }) => ({
                data: {
                    quantity: quantity > 0 ? quantity : 0,
                    sku: variant_sku || '',
                },
                parent_sku: parent_sku || '',
                variant_sku: variant_sku || '',
            })
        );

        console.log('[CartSync][BuildSyncOps] Sync operations built successfully', {
            updateItemsCount: updateItems.length,
            simpleProductItemsCount: simpleProductItems.length,
            configurableProductItemsCount: configurableProductItems.length,
            orphanedItemsCount: orphanedCartItemIds.length,
            updateItemsDetails: updateItems.map(item => ({
                cartItemId: item.cart_item_id,
                quantity: item.quantity,
                action: item.quantity === 0 ? 'DELETE' : 'UPDATE',
            })),
            orphanedItemsDetails: orphanedCartItemIds,
        });

        return {
            updateItems,
            simpleProductItems,
            configurableProductItems,
            referencesBySku, // Return for updating references after sync
            skuToCartItemId, // Return for updating references after sync
            orphanedCartItemIds, // Return for immediate cartStore cleanup
        };
    }

    /**
     * Execute update items mutation and return updated cart data
     * CRITICAL: Returns full cart from server to update cartStore
     */
    private async executeUpdateItems(cartId: string, updateItems: Array<{ cart_item_id: number; quantity: number }>) {
        const input: UpdateCartItemsInput = {
            cart_id: cartId,
            cart_items: updateItems,
        };

        const client = await this.getClient();
        if (!client) {
            throw new Error('Apollo Client is not initialized');
        }

        const result = await client.mutate({
            mutation: UpdateCartItemsDocument,
            variables: { input },
        });

        // Return cart data from server response to update cartStore
        return result.data?.updateCartItems?.cart;
    }

    /**
     * Execute add items mutation and return SKU to cart_item_id mapping plus cart response
     */
    private async executeAddItems(
        cartId: string,
        simpleProductItems: Array<{ data: { quantity: number; sku: string } }>
    ): Promise<{
        skuToNewCartItemId: Map<string, number>;
        cartResponse: AddSimpleProductsToCartOutput['cart'] | undefined;
    }> {
        const client = await this.getClient();
        if (!client) {
            throw new Error('Apollo Client is not initialized');
        }

        const createdResults: FetchResult<AddSimpleProductsToCartMutation> = await client.mutate({
            mutation: AddSimpleProductsToCartDocument,
            variables: {
                input: {
                    cart_id: cartId,
                    cart_items: simpleProductItems,
                },
            },
        });

        // Build mapping of SKU to cart_item_id
        const skuToCartItemId = new Map<string, number>();
        createdResults.data?.addSimpleProductsToCart?.cart.items?.forEach(item => {
            if (item?.__typename === 'SimpleCartItem' && (item as SimpleCartItem)?.product?.sku && item.id) {
                skuToCartItemId.set((item as SimpleCartItem)?.product?.sku ?? '', +(item?.id ?? 0));
            }
        });

        // Return both cart item mapping and cart response for updating cartStore
        return {
            skuToNewCartItemId: skuToCartItemId,
            cartResponse: createdResults.data?.addSimpleProductsToCart?.cart as AddSimpleProductsToCartOutput['cart'],
        };
    }

    /**
     * Execute add configurable items mutation and return SKU key to cart_item_id mapping plus cart response
     */
    private async executeAddConfigurableItems(
        cartId: string,
        configurableProductItems: Array<{
            data: { quantity: number; sku: string };
            parent_sku: string;
            variant_sku: string;
        }>
    ): Promise<{
        skuToNewCartItemId: Map<string, number>;
        cartResponse: AddConfigurableProductsToCartOutput['cart'];
    }> {
        const client = await this.getClient();
        if (!client) {
            throw new Error('Apollo Client is not initialized');
        }

        const createdResults: FetchResult<AddConfigurableProductsToCartMutation> = await client.mutate({
            mutation: AddConfigurableProductsToCartDocument,
            variables: {
                input: {
                    cart_id: cartId,
                    cart_items: configurableProductItems,
                },
            },
        });

        // Build mapping of "parent_sku|variant_sku" to cart_item_id
        const skuKeyToCartItemId = new Map<string, number>();

        // Create a map from input to match with results
        const inputBySku = new Map<string, { parent_sku: string; variant_sku: string }>();
        configurableProductItems.forEach(item => {
            inputBySku.set(item.data.sku, {
                parent_sku: item.parent_sku,
                variant_sku: item.variant_sku,
            });
        });

        createdResults.data?.addConfigurableProductsToCart?.cart.items?.forEach(item => {
            if (
                (item as ConfigurableCartItem)?.product?.sku &&
                item?.id &&
                item.__typename === 'ConfigurableCartItem'
            ) {
                const variantSku = (item as ConfigurableCartItem).configured_variant.sku ?? '';
                const inputData = inputBySku.get(variantSku);

                if (inputData) {
                    const skuKey = `${inputData.parent_sku}|${variantSku}`;
                    skuKeyToCartItemId.set(skuKey, +item.id);
                }
            }
        });

        // Return both cart item mapping and cart response for updating cartStore
        return {
            skuToNewCartItemId: skuKeyToCartItemId,
            cartResponse: createdResults.data?.addConfigurableProductsToCart
                ?.cart as AddConfigurableProductsToCartOutput['cart'],
        };
    }

    /**
     * ========================================================================
     * PROCESS GIFTS FOR REFERENCES
     * ========================================================================
     * After add/update operations, process references that have gift logics.
     * Collects all gift actions from references and adds them to cart using
     * AddGiftsToCart mutation. Server will override if gifts already exist.
     *
     * LOGIC:
     * 1. Filter references with gift logics (selected only)
     * 2. For each reference, get first server cart item id
     * 3. Collect all picked gifts from logics
     * 4. Group by cart_item_id and call AddGiftsToCart
     *
     * @param cartId Current cart ID
     * @param references All synced references (includes add/update results)
     */
    /**
     * Process gifts from references with gift logics
     * Collects all gifts and adds them to cart using AddGiftsToCart mutation
     * Collects cart update mutation instead of applying it directly
     */
    private async processGiftsForReferences(
        cartId: string,
        references: CartItemReference[],
        mutations: MutationCollector
    ): Promise<void> {
        console.log('[CartSync] Processing gifts for references', {
            cartId,
            totalReferences: references.length,
        });

        // Step 1: Filter selected references with gift logics
        const referencesWithGifts = references.filter(ref => {
            if (!ref.isSelected || !ref.logics || ref.logics.length === 0) {
                return false;
            }

            // Check if any logic has gift actions
            return ref.logics.some(logic =>
                logic.actions?.some(
                    action =>
                        action.actionType === ActionType.GIFT &&
                        action.actionProduct?.sku &&
                        action.actionQty &&
                        action.actionQty > 0
                )
            );
        });

        if (referencesWithGifts.length === 0) {
            console.log('[CartSync] No references with gift logics found');
            return;
        }

        // Step 2: Group references by cart_item_id
        // Each cart_item_id can have multiple gift logics
        const giftsByCartItemId = new Map<number, PickedGiftInput[]>();

        referencesWithGifts.forEach(ref => {
            // Get cart item id from reference
            let cartItemId: number | null = null;

            // Try to get from cartItem first
            if (ref.cartItem) {
                const itemId = Number(ref.cartItem.id ?? 0);
                if (itemId > 0) {
                    cartItemId = itemId;
                }
            }

            // Fallback: try cartItemId field
            if (!cartItemId && ref.cartItemId) {
                const itemId = Number(ref.cartItemId);
                if (itemId > 0) {
                    cartItemId = itemId;
                }
            }

            if (!cartItemId) {
                return;
            }

            // Collect gifts from logics
            const pickedGifts: PickedGiftInput[] = [];

            ref.logics?.forEach(logic => {
                const ruleId = Number(logic.logicId ?? 0);

                logic.actions?.forEach(action => {
                    if (
                        action.actionType === ActionType.GIFT &&
                        action.actionProduct?.sku &&
                        action.actionQty &&
                        action.actionQty > 0
                    ) {
                        pickedGifts.push({
                            rule_id: ruleId,
                            sku: action.actionProduct.sku,
                        });
                    }
                });
            });

            if (pickedGifts.length > 0) {
                // Merge with existing gifts for this cart item
                const existingGifts = giftsByCartItemId.get(cartItemId) || [];
                giftsByCartItemId.set(cartItemId, [...existingGifts, ...pickedGifts]);
            }
        });

        // Step 3: Call AddGiftsToCart for each cart_item_id
        const client = await this.getClient();
        if (!client) {
            console.error('[CartSync] Cannot process gifts: Apollo Client not initialized');
            return;
        }

        const addGiftsPromises: Promise<FetchResult<AddGiftsToCartMutation>>[] = [];

        giftsByCartItemId.forEach((pickedGifts, cartItemId) => {
            const input: AddGiftsToCartInput = {
                cart_id: cartId,
                cart_item_id: cartItemId,
                picked_gifts: pickedGifts,
            };

            const promise = client.mutate({
                mutation: AddGiftsToCartDocument,
                variables: { input },
            });

            addGiftsPromises.push(promise);
        });

        // Step 4: Execute all AddGiftsToCart mutations
        try {
            const results = await Promise.all(addGiftsPromises);

            // Collect cart update mutation from last response
            const lastResult = results[results.length - 1];
            if (lastResult.data?.addGiftsToCart?.cart) {
                const cartResponse = lastResult.data.addGiftsToCart.cart;
                // Type assertion is safe here - response contains all required Cart fields
                // Collect mutation - will be applied later
                mutations.setCart(cartResponse as Cart);
            }
        } catch (error) {
            console.error('[CartSync] Failed to add gifts:', error);

            const errorMessage = error instanceof Error ? error.message : String(error);
            flashMessageAPI.addWarning(errorMessage, 'Có lỗi xảy ra');

            // Don't throw - gifts are optional, continue with cart sync
        }
    }

    /**
     * Update cart_item_id for all references in the same SKU group
     * This is CRITICAL to prevent duplicate cart items when multiple references share the same SKU.
     * ENHANCED: Also update cartId for all references to ensure consistency
     *
     * ARCHITECTURE: Pure function - collects mutations
     * - No store access during execution
     * - Works with snapshot data for consistency
     * - Collects mutations instead of applying them
     */
    private async updateReferencesWithCartItemIds(
        referencesBySku: Map<string, CartItemReference[]>,
        skuToCartItemId: Map<string, number>,
        currentCartId: string,
        currentCartItemReferences: CartItemReference[],
        mutations: MutationCollector
    ) {
        if (!currentCartItemReferences) {
            return;
        }
        let updatedCount = 0;

        // For each SKU group, update ALL references with the same cart_item_id and cartId
        referencesBySku.forEach((references, skuKey) => {
            const cartItemId = skuToCartItemId.get(skuKey);
            if (!cartItemId) {
                return;
            }

            // Update ALL references in this SKU group with the same cart_item_id and cartId
            references.forEach(ref => {
                let needsUpdate = false;

                // CRITICAL: Update cartId for all references in this SKU group
                // This ensures R1 (has cartId) and R2 (newly selected) both have the same cartId
                if (currentCartId && ref.cartId !== currentCartId) {
                    ref.cartId = currentCartId;
                    needsUpdate = true;
                }

                // Update cartItemId
                if (ref.cartItemId !== String(cartItemId)) {
                    ref.cartItemId = String(cartItemId);
                    needsUpdate = true;
                }

                // Update cartItem
                if (ref.cartItem) {
                    const currentCartItemId = ref.cartItem.id;

                    // Update if cart_item_id is different or not set
                    if (currentCartItemId !== String(cartItemId)) {
                        ref.cartItem.id = String(cartItemId);
                        needsUpdate = true;
                    }
                }

                if (needsUpdate) {
                    updatedCount++;
                }
            });
        });

        if (updatedCount > 0) {
            // Collect mutation - will be applied later
            mutations.setAllReferences([...currentCartItemReferences]);
        }
    }

    /**
     * Ensure all cart items have corresponding references
     *
     * CRITICAL PRINCIPLE: cartStore is ONLY modified by server, never by local logic
     *
     * If cart items exist in cartStore but don't have references:
     * → CREATE new references (isSelected = false, cartId = undefined)
     * → DO NOT delete from cartStore (it's server data)
     * → User will see these items and can select/unselect them
     *
     * If references exist but cartId doesn't match:
     * → RESET reference (cartId = undefined, cartItemId = undefined)
     * → User needs to re-select to sync with new cart
     *
     * PROCESS:
     * 1. Find cart items without any reference
     * 2. Create new unselected references for them
     * 3. Find references with mismatched cartId
     * 4. Reset those references (clear cartId AND cartItemId, keep other data)
     *
     * ARCHITECTURE: Pure function - collects mutations
     * - No store access during execution
     * - Works with snapshot data for consistency
     * - Collects mutations instead of applying them
     */
    private async ensureReferencesForAllCartItems(
        currentCartItems: CartItemInterface[],
        allReferences: CartItemReference[],
        currentCartId: string,
        mutations: MutationCollector
    ) {
        if (!currentCartId) {
            return;
        }

        // Create a Map of cart items by their ID for quick lookup
        const cartItemsMap = new Map<string, CartItemInterface>();
        currentCartItems.forEach(item => {
            if (item.id && !item.id.startsWith('new-')) {
                cartItemsMap.set(String(item.id), item);
            }
        });

        // Create a Set of all cart item IDs that have references
        const referencedCartItemIds = new Set<string>();
        const referencesByCartItemId = new Map<string, CartItemReference[]>();

        allReferences.forEach(ref => {
            if (ref.cartItemId && !ref.cartItemId.startsWith('new-')) {
                const cartItemId = String(ref.cartItemId);
                referencedCartItemIds.add(cartItemId);

                if (!referencesByCartItemId.has(cartItemId)) {
                    referencesByCartItemId.set(cartItemId, []);
                }
                referencesByCartItemId.get(cartItemId)!.push(ref);
            }
        });

        // ========================================================================
        // STEP 1: Find cart items WITHOUT any reference → MAP or SKIP
        // ========================================================================
        // CRITICAL LOGIC: Prioritize local references
        //
        // For each server cart item without cartItemId mapping:
        // 1. Check if local has unselected reference with same SKU (not promotion)
        //    → Map server item to local reference (reuse local reference)
        // 2. If local doesn't have matching reference
        //    → Skip (don't create new reference here)
        //
        // WHY: Preserve local user state and prevent auto-creation
        const itemsWithoutReferences = currentCartItems.filter(item => {
            const itemId = String(item.id);
            return itemId && !itemId.startsWith('new-') && !referencedCartItemIds.has(itemId);
        });

        if (itemsWithoutReferences.length > 0) {
            console.log('[CartSync] Found server cart items without local references', {
                count: itemsWithoutReferences.length,
                items: itemsWithoutReferences.map(item => ({
                    id: item.id,
                    sku: item.product?.sku,
                    qty: (item as SimpleCartItem).quantity,
                })),
            });

            // Create Map of unselected local references by SKU for quick lookup
            const unselectedReferencesBySku = new Map<string, CartItemReference>();
            allReferences.forEach(ref => {
                // Only consider unselected references without cartItemId
                if (ref.isSelected === false && !ref.cartItemId) {
                    const sku = ref.cartItem?.product?.sku;
                    if (sku) {
                        // Check if NOT a promotion item (no logics or empty logics)
                        const hasPromotionLogics = ref.logics && ref.logics.length > 0;
                        if (!hasPromotionLogics) {
                            unselectedReferencesBySku.set(sku, ref);
                        }
                    }
                }
            });

            itemsWithoutReferences.forEach(cartItem => {
                // Detect gift items (price = 0) - skip them
                const itemPrice = cartItem.prices?.price?.value ?? 0;
                if (itemPrice === 0) {
                    console.log('[CartSync] Skipping gift item (price=0)', {
                        id: cartItem.id,
                        sku: cartItem.product?.sku,
                    });
                    return;
                }

                const serverItemSku = cartItem.product?.sku;
                if (!serverItemSku) {
                    console.log('[CartSync] Skipping item without SKU', {
                        id: cartItem.id,
                    });
                    return;
                }

                // CASE 1: Local has unselected reference with same SKU (not promotion)
                // → Map server item to local reference
                const localReference = unselectedReferencesBySku.get(serverItemSku);
                if (localReference) {
                    console.log('[CartSync] Mapping server item to existing local reference', {
                        serverItemId: cartItem.id,
                        serverSku: serverItemSku,
                        localReferenceId: localReference.cartItemReferenceId,
                        localSku: localReference.cartItem?.product?.sku,
                    });

                    // Update local reference with server data
                    const updatedReference: CartItemReference = {
                        ...localReference,
                        cartId: currentCartId, // Set to current cart
                        cartItemId: String(cartItem.id), // Map to server item
                        isSelected: true, // Mark as selected (server has it)
                        qty: (cartItem as SimpleCartItem).quantity, // Use server quantity
                        // Update cartItem with server data
                        cartItem: localReference.cartItem
                            ? {
                                  ...localReference.cartItem,
                                  id: String(cartItem.id), // Update with server cart item id
                                  quantity: (cartItem as SimpleCartItem).quantity,
                              }
                            : undefined,
                    };

                    // Collect mutation - will be applied later
                    mutations.addReferenceUpdate(updatedReference);
                    return;
                }

                // CASE 2: Local doesn't have matching reference
                // → Skip (don't create new reference here)
                console.log('[CartSync] Skipping server item without local reference', {
                    serverItemId: cartItem.id,
                    serverSku: serverItemSku,
                    reason: 'No matching unselected local reference found',
                });
            });
        }

        // ========================================================================
        // STEP 2: Find references with stale data → RESET or DELETE them
        // ========================================================================
        // Three scenarios to handle:
        // 1. Reference has cartId that doesn't match current cart → RESET
        // 2. Reference has cartItemId but server doesn't have that item anymore → RESET
        // 3. Reference is unselected and has no cartItemId → Keep as is (unselected state)

        const referencesToReset: CartItemReference[] = [];

        allReferences.forEach(ref => {
            // SCENARIO 1: Reference has cartId that doesn't match current cart
            // This happens when user switches between carts or cart ID changes
            if (ref.cartId && ref.cartId !== currentCartId) {
                console.log('[CartSync] Reference has mismatched cartId', {
                    referenceId: ref.cartItemReferenceId,
                    refCartId: ref.cartId,
                    currentCartId,
                });
                referencesToReset.push(ref);
                return;
            }

            // SCENARIO 2: Reference has cartItemId but server doesn't have that item
            // This happens when:
            // a) Item was unselected (isSelected=false) and sync deleted it from server
            // b) Item was deleted by another device/session
            // c) Server had an issue and item was removed
            if (ref.cartItemId && !ref.cartItemId.startsWith('new-')) {
                const cartItemId = String(ref.cartItemId);
                const existsOnServer = cartItemsMap.has(cartItemId);

                if (!existsOnServer) {
                    // Check if this is an unselected item (expected to be deleted)
                    if (ref.isSelected === false) {
                        console.log('[CartSync] Unselected reference - server deleted as expected', {
                            referenceId: ref.cartItemReferenceId,
                            cartItemId: ref.cartItemId,
                            sku: ref.cartItem?.product?.sku,
                        });
                        // RESET IDs but keep reference (user might want to reselect)
                        referencesToReset.push(ref);
                    } else {
                        console.log('[CartSync] Selected reference but item missing from server', {
                            referenceId: ref.cartItemReferenceId,
                            cartItemId: ref.cartItemId,
                            sku: ref.cartItem?.product?.sku,
                            isSelected: ref.isSelected,
                        });
                        // RESET IDs (item was deleted from server unexpectedly)
                        referencesToReset.push(ref);
                    }
                }
            }

            // SCENARIO 3: Reference is unselected and has no cartItemId
            // This is normal - reference is in "pending selection" state
            // No action needed - keep as is
        });

        if (referencesToReset.length > 0) {
            console.log('[CartSync] Resetting references with stale data', {
                count: referencesToReset.length,
            });

            referencesToReset.forEach(ref => {
                // RESET reference: clear ALL cart-related IDs
                // Preserve ALL other data (product info, qty, logics, isSelected state)
                const resetReference: CartItemReference = {
                    ...ref,
                    cartId: undefined, // Reset - not associated with any cart
                    cartItemId: undefined, // Reset - item needs to be re-added
                    // CRITICAL: Reset cartItem.id as well
                    cartItem: ref.cartItem
                        ? {
                              ...ref.cartItem,
                              id: '', // Reset (empty string for GraphQL type compliance)
                          }
                        : undefined,
                    // PRESERVED FIELDS:
                    // - isSelected: User's selection state preserved
                    // - qty: User's quantity choice preserved
                    // - productId: Product reference preserved
                    // - logics: Applied promotions/gifts preserved
                    // - cartItem data: Product info (SKU, name, price, image) preserved
                };

                console.log('[CartSync] Reset reference', {
                    referenceId: ref.cartItemReferenceId,
                    wasSelected: ref.isSelected,
                    hadCartItemId: !!ref.cartItemId,
                    sku: ref.cartItem?.product?.sku,
                });

                // Collect mutation - will be applied later
                mutations.addReferenceUpdate(resetReference);
            });
        }

        // Note: We don't delete references, we only reset them
        // This preserves user's product selections and allows them to reselect items
        // If we deleted, user would lose their cart configuration
    }
}
